import * as React from 'react';
import { cn } from '@/lib/utils';

export type ButtonVariant =
  | 'primary'     // Default - violet
  | 'secondary'   // Gray
  | 'accent'      // Gray (alias)
  | 'success'     // Green
  | 'danger'      // Red
  | 'warning'     // Amber
  | 'info'        // Blue
  | 'outline'     // Bordo solo
  | 'ghost'       // Trasparente
  | 'link';       // Testo underline

export type ButtonSize = 'default' | 'sm' | 'lg' | 'icon';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-700 active:bg-primary-800 shadow-sm',
  secondary: 'bg-secondary text-white hover:bg-secondary-700 active:bg-secondary-800 shadow-sm',
  accent: 'bg-accent text-white hover:bg-accent-700 active:bg-accent-800 shadow-sm',
  success: 'bg-success text-white hover:bg-success-700 active:bg-success-800 shadow-sm',
  danger: 'bg-danger text-white hover:bg-danger-700 active:bg-danger-800 shadow-sm',
  warning: 'bg-warning text-white hover:bg-warning-700 active:bg-warning-800 shadow-sm',
  info: 'bg-info text-white hover:bg-info-700 active:bg-info-800 shadow-sm',
  outline: 'border-2 border-gray-300 bg-transparent hover:bg-gray-100 hover:text-gray-900',
  ghost: 'hover:bg-gray-100 hover:text-gray-900',
  link: 'text-primary underline-offset-4 hover:underline',
};

const sizeClasses: Record<ButtonSize, string> = {
  default: 'h-10 px-4 py-2 rounded-md text-sm',
  sm: 'h-9 px-3 text-xs rounded-sm',
  lg: 'h-11 px-8 text-base rounded-lg',
  icon: 'h-10 w-10 rounded-md',
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'default', ...props }, ref) => {
    return (
      <button
        className={cn(
          'inline-flex items-center justify-center whitespace-nowrap font-medium',
          'transition-colors focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button };
